/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import { axiosFetch } from './axiosFetch';
import { getHeaders, postHeaders, parseURLParams } from '../utils';
import API_CONSTANTS from '../constants/apiConstants';
import constants from '../constants';

// Get translation status by project URN
// Real API implementation based on the provided curl request
export async function getTranslationStatusesByProject(dURN, entityURN) {
  const replacement = { '%dURN%': dURN, '%entityURN%': entityURN };
  const apiPath = parseURLParams(
    replacement,
    API_CONSTANTS.GET_TRANSLATION_STATUS_BY_PROJECT
  );

  const reqOptions = {
    ...getHeaders()
  };

  const errorHandlingProps = {
    skipError: true,
    codes: [404]
  };

  const response = await axiosFetch(
    reqOptions,
    apiPath,
    {},
    errorHandlingProps
  );
  if (response.data && response.data.success) {
    return response.data.data || [];
  }
  if (response.data && response.data.status === 404) {
    return [];
  }
  // Return empty array as fallback for any other case
  return [];
}

// Submit translation request
// Real API implementation based on the provided curl request
export async function submitTranslationRequest(translationData) {
  const {
    sourceProjectName, // Current project name (from projectDetail.name)
    targetProjectName, // New project title (from user input)
    sourceLanguage,
    targetLanguage,
    customInstructions,
    entityUrn,
    dURN,
    aiModel,
    glossaryId, // Selected glossary ID for translation
    glossaryFileName, // Selected glossary filename for translation
    instituteId // Institute ID of the source project
  } = translationData;

  // Use dURN for the API path
  const projectUrn = dURN;
  const replacement = { '%dURN%': projectUrn };
  const apiPath = parseURLParams(
    replacement,
    API_CONSTANTS.SUBMIT_TRANSLATION_REQUEST
  );

  // Create JSON payload with correct naming
  const payload = {
    entityUrn,
    sourceLanguage,
    targetLanguage,
    taskName: constants.TRANSLATE_PROJECT_TASK_NAME,
    sourceProjectName, // Actual current project name
    targetProjectName, // User-entered new project title
    aiModel,
    instituteID: instituteId
  };

  // Add custom instructions if provided
  if (customInstructions) {
    payload.customInstructions = customInstructions;
  }

  // Add glossary ID if provided
  if (glossaryId) {
    payload.glossaryId = glossaryId;
  }
  // Add glossary filename if provided
  if (glossaryFileName) {
    payload.glossaryFileName = glossaryFileName;
  }
  const reqOptions = {
    ...postHeaders(),
    body: payload
  };

  return axiosFetch(reqOptions, apiPath).then(response => {
    // API returns nested structure: { success, message, data: { success, message, correlationId, taskId, status } }
    if (response.data && response.data.success && response.data.data) {
      return {
        success: response.data.data.success,
        message: response.data.data.message,
        correlationId: response.data.data.correlationId,
        taskId: response.data.data.taskId,
        status: response.data.data.status
      };
    }
    throw new Error(
      response.data?.message || 'Failed to submit translation request'
    );
  });
}

// Get AI model and language mapping metadata
// Real API implementation based on the provided curl request
export async function getAiModelAndLanguageMapping() {
  const apiPath = API_CONSTANTS.GET_AI_MODEL_AND_LANGUAGE_MAPPING;

  const reqOptions = {
    ...getHeaders()
  };

  return axiosFetch(reqOptions, apiPath).then(response => {
    // API returns: { data: [...] }
    if (response.data && response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to retrieve AI model and language mapping');
  });
}

// Retry failed translation
export async function retryTranslation(dURN, taskId) {
  const replacement = { '%dURN%': dURN, '%taskId%': taskId };
  const apiPath = parseURLParams(replacement, API_CONSTANTS.RETRY_TRANSLATION);

  const reqOptions = {
    ...postHeaders()
  };

  try {
    const response = await axiosFetch(reqOptions, apiPath);
    if (response.data && response.data.statusCode === 200) {
      return {
        success: true,
        status: response.data.status,
        message: response.data.statusMessage,
        data: response.data.data
      };
    }
    throw new Error(
      response.data?.statusMessage || 'Failed to retry translation'
    );
  } catch (error) {
    throw new Error(error.message || 'Failed to retry translation');
  }
}

// Refresh translation status
export async function refreshTranslation(dURN, taskId) {
  const replacement = { '%dURN%': dURN, '%taskId%': taskId };
  const apiPath = parseURLParams(
    replacement,
    API_CONSTANTS.REFRESH_TRANSLATION
  );

  const reqOptions = {
    ...getHeaders()
  };

  try {
    const response = await axiosFetch(reqOptions, apiPath);
    if (response.data && response.data.statusCode === 200) {
      return {
        success: true,
        status: response.data.status,
        message: response.data.statusMessage,
        data: response.data.data
      };
    }
    throw new Error(
      response.data?.statusMessage || 'Failed to refresh translation'
    );
  } catch (error) {
    throw new Error(error.message || 'Failed to refresh translation');
  }
}
